import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PmsStaffMaster from './PmsStaffMaster';
import useIsMobile from '../hooks/useIsMobile';
import { todayIso } from '../utils/dateUtils';
const AUTO_REFRESH_MS = 30000; // 30 seconds


/* ── Notification sound using Web Audio API (no external file needed) ── */
const playAlertSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Two-tone chime: beep-boop
    [660, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.18, ctx.currentTime + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.3);
      osc.start(ctx.currentTime + i * 0.18);
      osc.stop(ctx.currentTime + i * 0.18 + 0.3);
    });
  } catch (_) { /* AudioContext not available */ }
};

/* ═══════════════════════════════════════════════════════════
   AI Optimizer Tab — Inline sub-component
   ═══════════════════════════════════════════════════════════ */
function AiOptimizerTab({ token, selectedProperty, headers }) {
  const [priorities, setPriorities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [acceptingId, setAcceptingId] = useState(null);
  const [acceptingAll, setAcceptingAll] = useState(false);

  // Turnaround metrics
  const [turnaround, setTurnaround] = useState(null);
  const [turnaroundFrom, setTurnaroundFrom] = useState(todayIso());
  const [turnaroundTo, setTurnaroundTo] = useState(todayIso());
  const [loadingTurnaround, setLoadingTurnaround] = useState(false);

  const hdrs = useCallback(() => headers(true), [headers]);

  // Load current priorities
  const loadPriorities = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoadingList(true);
    try {
      const res = await fetch('/api/v1/ai/housekeeping-optimizer/priorities', { headers: hdrs() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setPriorities(data.priorities || []);
      setSummary(data.summary || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingList(false);
    }
  }, [selectedProperty?.id, hdrs]);

  useEffect(() => { loadPriorities(); }, [loadPriorities]);

  // Generate priorities
  const handleGenerate = async () => {
    setGenerating(true); setError(''); setMessage('');
    try {
      const res = await fetch('/api/v1/ai/housekeeping-optimizer/generate', { method: 'POST', headers: hdrs() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      setMessage(`Generated ${data.total_priorities || 0} priorities. ${data.ai_summary || ''}`);
      loadPriorities();
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  // Accept single
  const handleAccept = async (id) => {
    setAcceptingId(id);
    try {
      const res = await fetch(`/api/v1/ai/housekeeping-optimizer/priorities/${id}/accept`, { method: 'POST', headers: hdrs() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Accept failed');
      setMessage('Priority accepted — housekeeping task created');
      loadPriorities();
    } catch (err) {
      setError(err.message);
    } finally {
      setAcceptingId(null);
    }
  };

  // Accept all
  const handleAcceptAll = async () => {
    setAcceptingAll(true);
    try {
      const res = await fetch('/api/v1/ai/housekeeping-optimizer/priorities/accept-all', { method: 'POST', headers: hdrs() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Accept all failed');
      setMessage(`Accepted ${data.accepted_count || 0} priorities — tasks created`);
      loadPriorities();
    } catch (err) {
      setError(err.message);
    } finally {
      setAcceptingAll(false);
    }
  };

  // Skip
  const handleSkip = async (id) => {
    try {
      const res = await fetch(`/api/v1/ai/housekeeping-optimizer/priorities/${id}/skip`, { method: 'POST', headers: hdrs() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Skip failed');
      loadPriorities();
    } catch (err) {
      setError(err.message);
    }
  };

  // Turnaround metrics
  const loadTurnaround = async () => {
    setLoadingTurnaround(true);
    try {
      const res = await fetch(`/api/v1/ai/housekeeping-optimizer/turnaround?from=${turnaroundFrom}&to=${turnaroundTo}`, { headers: hdrs() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setTurnaround(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTurnaround(false);
    }
  };

  const urgencyColor = { CRITICAL: 'var(--theme-danger)', HIGH: '#ea580c', NORMAL: '#2563eb', LOW: 'var(--theme-text-muted)' };
  const urgencyBg = { CRITICAL: '#fef2f2', HIGH: '#fff7ed', NORMAL: '#eff6ff', LOW: 'var(--theme-bg-light)' };
  const taskTypeBg = { CHECKOUT_CLEAN: '#fef3c7', STAYOVER_CLEAN: '#dbeafe', VIP_PREP: '#f3e8ff', EARLY_CHECKIN_PREP: '#fce7f3', DEEP_CLEAN: '#d1fae5' };
  const taskTypeColor = { CHECKOUT_CLEAN: '#92400e', STAYOVER_CLEAN: '#1e40af', VIP_PREP: '#6b21a8', EARLY_CHECKIN_PREP: '#9d174d', DEEP_CLEAN: '#065f46' };

  const pendingCount = priorities.filter(p => p.status === 'PENDING').length;

  return (
    <div style={{ marginTop: '0.8rem' }}>
      {error && <div className="error-banner" style={{ marginBottom: '0.5rem' }}>{error}</div>}
      {message && <div className="success-banner" style={{ marginBottom: '0.5rem' }}>{message}</div>}

      {/* ── Action Bar ── */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
        <button onClick={handleGenerate} disabled={generating}
          style={{ padding: '0.45rem 1rem', borderRadius: 6, border: 'none', background: '#0ea5e9', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: generating ? 'wait' : 'pointer', opacity: generating ? 0.7 : 1 }}>
          {generating ? 'Generating...' : 'Generate Priorities'}
        </button>
        {pendingCount > 0 && (
          <button onClick={handleAcceptAll} disabled={acceptingAll}
            style={{ padding: '0.45rem 1rem', borderRadius: 6, border: '1px solid #22c55e', background: '#f0fdf4', color: '#166534', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
            {acceptingAll ? 'Accepting...' : `Accept All (${pendingCount})`}
          </button>
        )}
        <button onClick={loadPriorities} disabled={loadingList}
          style={{ padding: '0.35rem 0.8rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
          {loadingList ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* ── Summary KPIs ── */}
      {summary && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
          <span style={{ padding: '4px 12px', borderRadius: 20, background: 'var(--theme-border-light)', color: '#334155', fontWeight: 700, fontSize: '0.82rem' }}>
            Total: {summary.total || 0}
          </span>
          {(summary.critical || 0) > 0 && (
            <span style={{ padding: '4px 12px', borderRadius: 20, background: '#fef2f2', color: 'var(--theme-danger)', fontWeight: 700, fontSize: '0.82rem', border: '1px solid #fecaca' }}>
              Critical: {summary.critical}
            </span>
          )}
          {(summary.high || 0) > 0 && (
            <span style={{ padding: '4px 12px', borderRadius: 20, background: '#fff7ed', color: '#ea580c', fontWeight: 700, fontSize: '0.82rem', border: '1px solid #fed7aa' }}>
              High: {summary.high}
            </span>
          )}
          {(summary.normal || 0) > 0 && (
            <span style={{ padding: '4px 12px', borderRadius: 20, background: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: '0.82rem', border: '1px solid #bfdbfe' }}>
              Normal: {summary.normal}
            </span>
          )}
          {(summary.low || 0) > 0 && (
            <span style={{ padding: '4px 12px', borderRadius: 20, background: 'var(--theme-bg-light)', color: 'var(--theme-text-muted)', fontWeight: 700, fontSize: '0.82rem', border: '1px solid var(--theme-border)' }}>
              Low: {summary.low}
            </span>
          )}
        </div>
      )}

      {/* ── Priority Cards ── */}
      {priorities.length === 0 && !loadingList && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: '0.9rem' }}>
          No AI priorities generated yet. Click "Generate Priorities" to analyze rooms.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {priorities.map((p) => (
          <div key={p.id} style={{
            background: p.status === 'ACCEPTED' ? '#f0fdf4' : p.status === 'SKIPPED' ? 'var(--theme-bg-light)' : urgencyBg[p.urgency] || '#fff',
            border: `1px solid ${p.status === 'ACCEPTED' ? '#86efac' : p.status === 'SKIPPED' ? 'var(--theme-border)' : (urgencyColor[p.urgency] || '#cbd5e1')}40`,
            borderRadius: 8, padding: '0.7rem 0.9rem', opacity: p.status === 'SKIPPED' ? 0.6 : 1
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.4rem' }}>
              {/* Left: rank + room info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flex: 1 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, borderRadius: '50%',
                  background: urgencyColor[p.urgency] || 'var(--theme-text-secondary)', color: '#fff',
                  fontWeight: 800, fontSize: '0.82rem'
                }}>#{p.priority_rank}</span>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>{p.room_number}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)' }}>Floor {p.floor_no}</span>
                    {p.room_type_name && <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)' }}>{p.room_type_name}</span>}
                    <span style={{
                      fontSize: '0.72rem', padding: '1px 8px', borderRadius: 4, fontWeight: 700,
                      background: taskTypeBg[p.task_type] || 'var(--theme-border-light)', color: taskTypeColor[p.task_type] || '#475569'
                    }}>{(p.task_type || '').replace(/_/g, ' ')}</span>
                    <span style={{
                      fontSize: '0.72rem', padding: '1px 8px', borderRadius: 4, fontWeight: 700,
                      background: urgencyBg[p.urgency] || 'var(--theme-border-light)', color: urgencyColor[p.urgency] || '#475569',
                      border: `1px solid ${urgencyColor[p.urgency] || '#cbd5e1'}40`
                    }}>{p.urgency}</span>
                    {p.is_vip && <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4, background: '#f3e8ff', color: '#6b21a8', fontWeight: 700 }}>VIP</span>}
                    {p.is_corporate && <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4, background: '#fef3c7', color: '#92400e', fontWeight: 700 }}>CORP</span>}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: 2 }}>{p.reason}</div>
                  <div style={{ display: 'flex', gap: '0.6rem', fontSize: '0.78rem', color: 'var(--theme-text-muted)', marginTop: 2, flexWrap: 'wrap' }}>
                    {p.assigned_staff_name && <span>Staff: <b style={{ color: '#334155' }}>{p.assigned_staff_name}</b></span>}
                    {p.turnaround_window != null && <span>Window: <b style={{ color: '#334155' }}>{p.turnaround_window} min</b></span>}
                    {p.guest_name && <span>Guest: {p.guest_name}</span>}
                  </div>
                </div>
              </div>
              {/* Right: actions */}
              <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                {p.status === 'PENDING' ? (
                  <>
                    <button onClick={() => handleAccept(p.id)} disabled={acceptingId === p.id}
                      style={{ padding: '4px 12px', borderRadius: 5, border: 'none', background: '#166534', color: '#fff', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
                      {acceptingId === p.id ? '...' : 'Accept'}
                    </button>
                    <button onClick={() => handleSkip(p.id)}
                      style={{ padding: '4px 12px', borderRadius: 5, border: '1px solid #cbd5e1', background: '#fff', color: 'var(--theme-text-secondary)', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
                      Skip
                    </button>
                  </>
                ) : (
                  <span style={{ fontSize: '0.78rem', fontWeight: 700,
                    color: p.status === 'ACCEPTED' ? '#166534' : p.status === 'SKIPPED' ? 'var(--theme-text-muted)' : '#1d4ed8'
                  }}>{p.status}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Performance Metrics ── */}
      <div className="card card-body" style={{ marginTop: '1.2rem' }}>
        <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.5rem' }}>Turnaround Performance</div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>From:</label>
          <input type="date" value={turnaroundFrom} onChange={e => setTurnaroundFrom(e.target.value)}
            style={{ padding: '0.25rem 0.5rem', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: '0.82rem' }} />
          <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>To:</label>
          <input type="date" value={turnaroundTo} onChange={e => setTurnaroundTo(e.target.value)}
            style={{ padding: '0.25rem 0.5rem', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: '0.82rem' }} />
          <button onClick={loadTurnaround} disabled={loadingTurnaround}
            style={{ padding: '0.3rem 0.8rem', borderRadius: 5, border: '1px solid #cbd5e1', background: 'var(--theme-primary)', color: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
            {loadingTurnaround ? 'Loading...' : 'Load Metrics'}
          </button>
        </div>
        {turnaround && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            {/* Overall */}
            {turnaround.overall && turnaround.overall.total_tasks > 0 && (
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ background: '#eff6ff', padding: '0.5rem 1rem', borderRadius: 6 }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>Total Tasks</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e40af' }}>{turnaround.overall.total_tasks}</div>
                </div>
                <div style={{ background: '#f0fdf4', padding: '0.5rem 1rem', borderRadius: 6 }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>Avg Turnaround</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#166534' }}>{turnaround.overall.avg_turnaround || '-'} min</div>
                </div>
                <div style={{ background: '#faf5ff', padding: '0.5rem 1rem', borderRadius: 6 }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>Avg Cleaning</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#6b21a8' }}>{turnaround.overall.avg_cleaning || '-'} min</div>
                </div>
              </div>
            )}
            {/* By Staff */}
            {turnaround.by_staff?.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155', marginBottom: '0.3rem' }}>By Staff</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead><tr style={{ background: 'var(--theme-border-light)' }}>
                    <th style={{ padding: '0.35rem', textAlign: 'left' }}>Staff</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Tasks</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Avg Turnaround</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Avg Cleaning</th>
                  </tr></thead>
                  <tbody>{turnaround.by_staff.map((s, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={{ padding: '0.35rem', fontWeight: 600 }}>{s.staff_name || '-'}</td>
                      <td style={{ padding: '0.35rem', textAlign: 'right' }}>{s.tasks_completed}</td>
                      <td style={{ padding: '0.35rem', textAlign: 'right' }}>{s.avg_turnaround || '-'} min</td>
                      <td style={{ padding: '0.35rem', textAlign: 'right' }}>{s.avg_cleaning || '-'} min</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {/* By Floor */}
            {turnaround.by_floor?.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155', marginBottom: '0.3rem' }}>By Floor</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead><tr style={{ background: 'var(--theme-border-light)' }}>
                    <th style={{ padding: '0.35rem', textAlign: 'left' }}>Floor</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Tasks</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Avg Turnaround</th>
                    <th style={{ padding: '0.35rem', textAlign: 'right' }}>Avg Cleaning</th>
                  </tr></thead>
                  <tbody>{turnaround.by_floor.map((f, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={{ padding: '0.35rem', fontWeight: 600 }}>Floor {f.floor_no}</td>
                      <td style={{ padding: '0.35rem', textAlign: 'right' }}>{f.tasks_completed}</td>
                      <td style={{ padding: '0.35rem', textAlign: 'right' }}>{f.avg_turnaround || '-'} min</td>
                      <td style={{ padding: '0.35rem', textAlign: 'right' }}>{f.avg_cleaning || '-'} min</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {(!turnaround.overall || turnaround.overall.total_tasks === 0) && (
              <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>No turnaround data for selected date range.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PmsHousekeeping({ token, selectedProperty, aiOptimizerEnabled }) {
  const { isMobile } = useIsMobile();
  const [hkTab, setHkTab] = useState('dashboard');
  const [rooms, setRooms] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tasks, setTasks] = useState([]); // ER-MOM-03: today's cleaning tasks (for live SLA state)
  const [slaBannerDismissed, setSlaBannerDismissed] = useState(false);
  // ER-MOM-04: staff performance leaderboard
  const [leaderboard, setLeaderboard] = useState(null);
  const [lbFrom, setLbFrom] = useState(todayIso());
  const [lbTo, setLbTo] = useState(todayIso());
  const [lbLoading, setLbLoading] = useState(false);
  // ER-MOM-03 Phase 2: SLA-breach report
  const [slaReport, setSlaReport] = useState(null);
  const [srFrom, setSrFrom] = useState(todayIso());
  const [srTo, setSrTo] = useState(todayIso());
  const [srLoading, setSrLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [hkLog, setHkLog] = useState([]);
  const [hkLogLoading, setHkLogLoading] = useState(false);
  const [hkLogRoomFilter, setHkLogRoomFilter] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState('OPEN');
  const [updatingRoomId, setUpdatingRoomId] = useState('');
  const [rackFloorFilter, setRackFloorFilter] = useState('');
  const [rackStatusFilter, setRackStatusFilter] = useState('');
  const [confirmRoom, setConfirmRoom] = useState(null);
  const [hkStaffList, setHkStaffList] = useState([]); // ER-MOM-04: HK staff for "cleaned by" attribution
  const [cleanStaffId, setCleanStaffId] = useState('');
  const [newDirtyRooms, setNewDirtyRooms] = useState([]);
  const [activeRequests, setActiveRequests] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoRefreshCountdown, setAutoRefreshCountdown] = useState(AUTO_REFRESH_MS / 1000);

  const prevDirtySetRef = useRef(new Set());
  const autoRefreshRef = useRef(null);
  const countdownRef = useRef(null);

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty?.chain_id, selectedProperty?.id]);

  const load = useCallback(async (silent = false) => {
    if (!selectedProperty?.id) {
      setRooms([]);
      setRequests([]);
      return;
    }
    if (!silent) setLoading(true);
    if (!silent) setError('');
    try {
      const [roomRes, reqRes, activeReqRes, taskRes] = await Promise.all([
        fetch(`/api/v1/pms/room-rack/live?date=${encodeURIComponent(todayIso())}`, { headers: headers() }),
        fetch(`/api/v1/pms/housekeeping/requests?status=${encodeURIComponent(requestStatusFilter)}`, { headers: headers() }),
        fetch('/api/v1/pms/housekeeping/requests?status=', { headers: headers() }).catch(() => null),
        fetch(`/api/v1/pms/housekeeping/tasks?date=${encodeURIComponent(todayIso())}`, { headers: headers() }).catch(() => null)
      ]);
      const roomData = await roomRes.json();
      const reqData = await reqRes.json();
      if (!roomRes.ok) throw new Error(roomData?.error || 'Failed to load rooms');
      if (!reqRes.ok) throw new Error(reqData?.error || 'Failed to load requests');
      // Cleaning tasks (for live SLA state) — non-fatal if it fails
      if (taskRes && taskRes.ok) {
        const taskData = await taskRes.json().catch(() => []);
        setTasks(Array.isArray(taskData) ? taskData : []);
      }
      // Active requests for priority badges
      if (activeReqRes && activeReqRes.ok) {
        const allReq = await activeReqRes.json().catch(() => []);
        setActiveRequests(Array.isArray(allReq) ? allReq.filter(r => ['OPEN', 'IN_PROGRESS'].includes(r.request_status)) : []);
      }
      const roomArr = Array.isArray(roomData) ? roomData : [];

      // Detect newly dirty rooms
      const currentDirtySet = new Set();
      roomArr.forEach((r) => {
        if (String(r.housekeeping_status || '').toUpperCase() === 'DIRTY') currentDirtySet.add(String(r.room_id));
      });
      if (prevDirtySetRef.current.size > 0 || roomArr.length > 0) {
        const newlyDirty = [];
        currentDirtySet.forEach((id) => {
          if (!prevDirtySetRef.current.has(id)) {
            const rm = roomArr.find((r) => String(r.room_id) === id);
            if (rm) newlyDirty.push(rm.room_number);
          }
        });
        if (newlyDirty.length > 0) {
          setNewDirtyRooms(newlyDirty);
          if (soundEnabled) playAlertSound();
        }
      }
      prevDirtySetRef.current = currentDirtySet;

      setRooms(roomArr);
      setRequests(Array.isArray(reqData) ? reqData : []);
    } catch (err) {
      if (!silent) setError(err?.message || 'Failed to load housekeeping data');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedProperty?.id, requestStatusFilter, headers, soundEnabled]);

  // Initial load
  useEffect(() => {
    load();
  }, [load]);

  // ER-MOM-04: HK staff roster for the "cleaned by" picker (attributes tasks).
  useEffect(() => {
    if (!selectedProperty?.id) { setHkStaffList([]); return; }
    fetch('/api/v1/pms/staff?department=HOUSEKEEPING', { headers: headers() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setHkStaffList(Array.isArray(d) ? d.filter((s) => s.is_active !== false) : []))
      .catch(() => setHkStaffList([]));
  }, [selectedProperty?.id, headers]);

  // Auto-refresh every 30s on dashboard tab
  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (hkTab !== 'dashboard' || !selectedProperty?.id) return;

    setAutoRefreshCountdown(AUTO_REFRESH_MS / 1000);
    countdownRef.current = setInterval(() => {
      setAutoRefreshCountdown((prev) => (prev <= 1 ? AUTO_REFRESH_MS / 1000 : prev - 1));
    }, 1000);
    autoRefreshRef.current = setInterval(() => {
      load(true); // silent refresh
    }, AUTO_REFRESH_MS);

    return () => {
      clearInterval(autoRefreshRef.current);
      clearInterval(countdownRef.current);
    };
  }, [hkTab, selectedProperty?.id, load]);

  /* ── Room Rack helpers ── */
  const floors = useMemo(() => {
    const set = new Set();
    rooms.forEach((r) => { if (r.floor_no) set.add(String(r.floor_no)); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [rooms]);

  const filteredRooms = useMemo(() => {
    return rooms
      .filter((r) => {
        if (rackFloorFilter && String(r.floor_no) !== rackFloorFilter) return false;
        const hk = String(r.housekeeping_status || '').toUpperCase();
        const occ = String(r.occupancy_status || '').toUpperCase();
        const maint = String(r.maintenance_status || '').toUpperCase();
        if (rackStatusFilter === 'DIRTY') return hk === 'DIRTY';
        if (rackStatusFilter === 'CLEAN') return hk !== 'DIRTY' && occ === 'VACANT' && !['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(maint);
        if (rackStatusFilter === 'OCCUPIED') return occ === 'OCCUPIED';
        return true;
      })
      .sort((a, b) => String(a.room_number || '').localeCompare(String(b.room_number || ''), undefined, { numeric: true }));
  }, [rooms, rackFloorFilter, rackStatusFilter]);

  const rackSummary = useMemo(() => {
    let total = 0, dirty = 0, clean = 0, occupied = 0, ooo = 0;
    rooms.forEach((r) => {
      total++;
      const hk = String(r.housekeeping_status || '').toUpperCase();
      const occ = String(r.occupancy_status || '').toUpperCase();
      const maint = String(r.maintenance_status || '').toUpperCase();
      if (['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(maint)) { ooo++; return; }
      if (occ === 'OCCUPIED') { occupied++; return; }
      if (hk === 'DIRTY') { dirty++; return; }
      clean++;
    });
    return { total, dirty, clean, occupied, ooo };
  }, [rooms]);

  const tileStyle = (r) => {
    const hk = String(r.housekeeping_status || '').toUpperCase();
    const occ = String(r.occupancy_status || '').toUpperCase();
    const maint = String(r.maintenance_status || '').toUpperCase();
    const isOoo = ['OUT_OF_ORDER', 'PAINTING', 'BLOCKED'].includes(maint);
    const isOccupied = occ === 'OCCUPIED';
    const isDirty = hk === 'DIRTY';
    const isActionable = !isOoo && !isOccupied;

    let border = '#22c55e', cardBg = '#f0fdf4', badgeBg = '#22c55e', label = 'CLEAN';
    if (isOoo) { border = '#ef4444'; cardBg = '#fef2f2'; badgeBg = '#ef4444'; label = maint.replace(/_/g, ' '); }
    else if (isOccupied) { border = '#3b82f6'; cardBg = '#eff6ff'; badgeBg = '#3b82f6'; label = 'OCCUPIED'; }
    else if (isDirty) { border = '#f59e0b'; cardBg = '#fffbeb'; badgeBg = '#f59e0b'; label = 'DIRTY'; }

    return { border, cardBg, badgeBg, label, isActionable, isDirty };
  };

  // Map room_id → active priority request for badge
  const reqByRoom = useMemo(() => {
    const map = {};
    activeRequests.forEach((r) => { const rid = String(r.room_id); if (!map[rid]) map[rid] = r; });
    return map;
  }, [activeRequests]);

  /* ── ER-MOM-03: live SLA state per room ──
     A cleaning task is BREACHED when it's been running longer than its room
     type's SLA (or the server checker already flagged it), and APPROACHING at
     ≥80% of the SLA. We compute elapsed client-side from start_at so the tile
     updates on every 30s refresh, earlier than the 5-min server checker/SMS. */
  const SLA_WARN_RATIO = 0.8;
  const slaByRoom = useMemo(() => {
    const map = {};
    const active = new Set(['ASSIGNED', 'IN_PROGRESS', 'REOPENED']);
    (tasks || []).forEach((t) => {
      const sla = Number(t.sla_minutes);
      if (!active.has(String(t.task_status || '').toUpperCase())) return;
      if (!t.start_at || t.end_at || !sla) return; // needs a running clock + a configured SLA
      const elapsed = Math.floor((Date.now() - new Date(t.start_at).getTime()) / 60000);
      if (!(elapsed >= 0)) return;
      const breached = t.sla_breached === true || elapsed >= sla;
      const approaching = !breached && elapsed >= sla * SLA_WARN_RATIO;
      if (!breached && !approaching) return;
      const rid = String(t.room_id);
      const entry = { state: breached ? 'BREACHED' : 'APPROACHING', elapsed, sla, room_number: t.room_number, housekeeper: t.assigned_to_name };
      // Keep the most severe per room (BREACHED wins).
      if (!map[rid] || (entry.state === 'BREACHED' && map[rid].state !== 'BREACHED')) map[rid] = entry;
    });
    return map;
  }, [tasks]);

  const slaBreachedRooms = useMemo(
    () => Object.values(slaByRoom).filter((s) => s.state === 'BREACHED'),
    [slaByRoom]
  );
  const slaApproachingCount = useMemo(
    () => Object.values(slaByRoom).filter((s) => s.state === 'APPROACHING').length,
    [slaByRoom]
  );

  // Re-show a dismissed SLA banner when a NEW room breaches (don't stay hidden
  // through a fresh breach). Keyed on the sorted set of breached room numbers.
  const slaBreachSig = useMemo(
    () => slaBreachedRooms.map((s) => s.room_number).sort().join(','),
    [slaBreachedRooms]
  );
  const prevSlaSigRef = useRef('');
  useEffect(() => {
    if (slaBreachSig && slaBreachSig !== prevSlaSigRef.current) setSlaBannerDismissed(false);
    prevSlaSigRef.current = slaBreachSig;
  }, [slaBreachSig]);

  const priorityBadge = (reqType) => {
    const t = String(reqType || '').toUpperCase();
    if (t === 'VIP_CLEANING') return { bg: '#7c3aed', label: 'VIP' };
    if (t === 'EARLY_CHECKIN_PREP') return { bg: '#2563eb', label: 'EARLY C/I' };
    return { bg: '#ea580c', label: 'EXPRESS' };
  };

  /* ── Toggle housekeeping status ── */
  const toggleHousekeeping = async (room, staffId) => {
    const hk = String(room.housekeeping_status || '').toUpperCase();
    const nextStatus = hk === 'DIRTY' ? 'CLEAN' : 'DIRTY';
    setConfirmRoom(null);
    setUpdatingRoomId(String(room.room_id));
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/pms/rooms/${room.room_id}/housekeeping`, {
        method: 'POST',
        headers: headers(true),
        // staff_id (optional) credits the cleaner to the auto-completed task → leaderboard.
        body: JSON.stringify({ status: nextStatus, source: 'HOUSEKEEPING', staff_id: staffId || null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update status');
      setMessage(`Room ${room.room_number} marked as ${nextStatus}`);
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to update housekeeping status');
    } finally {
      setUpdatingRoomId('');
    }
  };

  const handleTileClick = (room) => {
    const { isActionable } = tileStyle(room);
    if (!isActionable) return;
    setCleanStaffId('');
    setConfirmRoom(room);
  };

  const runRequestAction = async (requestId, newStatus) => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/pms/housekeeping/requests/${requestId}/status`, {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to update request');
      setMessage(`Request updated to ${newStatus.replace(/_/g, ' ')}`);
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to update request');
    }
  };

  /* ── Cleaning Log ── */
  const loadHkLog = async () => {
    if (!selectedProperty?.id) return setHkLog([]);
    setHkLogLoading(true);
    try {
      const qs = hkLogRoomFilter ? `&room_id=${hkLogRoomFilter}` : '';
      const res = await fetch(`/api/v1/pms/housekeeping/log?limit=50${qs}`, { headers: headers() });
      const data = await res.json().catch(() => []);
      setHkLog(Array.isArray(data) ? data : []);
    } catch (_) { setHkLog([]); }
    finally { setHkLogLoading(false); }
  };

  useEffect(() => {
    if (hkTab === 'log') loadHkLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hkTab, selectedProperty?.id, hkLogRoomFilter]);

  /* ── ER-MOM-04: Leaderboard ── */
  const loadLeaderboard = async () => {
    if (!selectedProperty?.id) return setLeaderboard(null);
    setLbLoading(true);
    try {
      const res = await fetch(`/api/v1/pms/housekeeping/leaderboard?from=${lbFrom}&to=${lbTo}`, { headers: headers() });
      const data = await res.json().catch(() => null);
      if (res.ok) setLeaderboard(data);
      else { setLeaderboard(null); setError(data?.error || 'Failed to load leaderboard'); }
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setLbLoading(false); }
  };

  useEffect(() => {
    if (hkTab === 'leaderboard') loadLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hkTab, selectedProperty?.id]);

  /* ── ER-MOM-03 Phase 2: SLA-breach report ── */
  const loadSlaReport = async () => {
    if (!selectedProperty?.id) return setSlaReport(null);
    setSrLoading(true);
    try {
      const res = await fetch(`/api/v1/pms/housekeeping/sla-report?from=${srFrom}&to=${srTo}`, { headers: headers() });
      const data = await res.json().catch(() => null);
      if (res.ok) setSlaReport(data);
      else { setSlaReport(null); setError(data?.error || 'Failed to load SLA report'); }
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setSrLoading(false); }
  };

  useEffect(() => {
    if (hkTab === 'sla-report') loadSlaReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hkTab, selectedProperty?.id]);

  const hkTabBtn = (active) => ({
    padding: '0.5rem 0.85rem', borderRadius: '7px', border: '1px solid #cbd5e0',
    background: active ? 'var(--theme-primary)' : '#fff', color: active ? '#fff' : '#1f2937', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem'
  });

  const chipBase = { padding: '0.3rem 0.7rem', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' };

  const summaryBadge = (label, count, bg, color, filterVal) => (
    <span onClick={() => setRackStatusFilter(rackStatusFilter === filterVal ? '' : filterVal)}
      style={{ ...chipBase, background: bg, color, border: rackStatusFilter === filterVal ? `2px solid ${color}` : `1px solid ${bg}`,
        boxShadow: rackStatusFilter === filterVal ? `0 0 0 2px ${color}30` : 'none' }}>
      {label}: {count}
    </span>
  );

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.6rem', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
        <h3 style={{ margin: 0, color: 'var(--theme-primary)', fontSize: isMobile ? '0.95rem' : undefined }}>Front Office - Housekeeping</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setHkTab('dashboard')} style={hkTabBtn(hkTab === 'dashboard')}>Dashboard</button>
          <button onClick={() => setHkTab('leaderboard')} style={hkTabBtn(hkTab === 'leaderboard')}>Performance</button>
          <button onClick={() => setHkTab('sla-report')} style={hkTabBtn(hkTab === 'sla-report')}>SLA Report</button>
          <button onClick={() => setHkTab('log')} style={hkTabBtn(hkTab === 'log')}>Cleaning Log</button>
          <button onClick={() => setHkTab('staff')} style={hkTabBtn(hkTab === 'staff')}>Staff Master</button>
          {aiOptimizerEnabled && <button onClick={() => setHkTab('ai-optimizer')} style={hkTabBtn(hkTab === 'ai-optimizer')}>AI Optimizer</button>}
        </div>
      </div>

      {hkTab === 'staff' ? (
        <PmsStaffMaster token={token} selectedProperty={selectedProperty} />
      ) : hkTab === 'ai-optimizer' ? (
        <AiOptimizerTab token={token} selectedProperty={selectedProperty} headers={headers} />
      ) : hkTab === 'leaderboard' ? (
        <div style={{ marginTop: '0.8rem' }}>
          {error ? <div className="error-banner" style={{ marginBottom: '0.5rem' }}>{error}</div> : null}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>From:</label>
            <input type="date" value={lbFrom} onChange={(e) => setLbFrom(e.target.value)} max={lbTo}
              style={{ padding: '0.3rem 0.5rem', borderRadius: 5, border: '1px solid #cbd5e1', fontSize: '0.85rem' }} />
            <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>To:</label>
            <input type="date" value={lbTo} onChange={(e) => setLbTo(e.target.value)} min={lbFrom}
              style={{ padding: '0.3rem 0.5rem', borderRadius: 5, border: '1px solid #cbd5e1', fontSize: '0.85rem' }} />
            <button onClick={loadLeaderboard} disabled={lbLoading}
              style={{ padding: '0.35rem 1rem', borderRadius: 5, border: 'none', background: 'var(--theme-primary)', color: '#fff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
              {lbLoading ? 'Loading...' : 'Load'}
            </button>
          </div>

          {leaderboard?.overall && (
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
              {[
                { label: 'Housekeepers', val: leaderboard.overall.housekeepers, bg: '#eff6ff', color: '#1e40af' },
                { label: 'Rooms Cleaned', val: leaderboard.overall.completed, bg: '#f0fdf4', color: '#166534' },
                { label: 'Avg Cleaning', val: leaderboard.overall.avg_cleaning_mins != null ? `${leaderboard.overall.avg_cleaning_mins} min` : '-', bg: '#faf5ff', color: '#6b21a8' },
                { label: 'SLA Breaches', val: leaderboard.overall.sla_breaches, bg: '#fef2f2', color: '#b91c1c' },
                { label: 'Re-cleans', val: leaderboard.overall.reopened, bg: '#fff7ed', color: '#c2410c' },
              ].map((k) => (
                <div key={k.label} style={{ background: k.bg, padding: '0.5rem 1rem', borderRadius: 6, minWidth: 96 }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>{k.label}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: k.color }}>{k.val}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem', background: '#fff' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>#</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Housekeeper</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Cleaned</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Avg Time</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>On-Time</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>SLA Breach</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Re-clean</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {(leaderboard?.staff || []).map((s) => {
                  const medal = s.rank === 1 ? '🥇' : s.rank === 2 ? '🥈' : s.rank === 3 ? '🥉' : null;
                  return (
                    <tr key={`${s.staff_id ?? 'n'}-${s.staff_name}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={{ padding: '0.45rem', fontWeight: 700 }}>{medal || s.rank}</td>
                      <td style={{ padding: '0.45rem', fontWeight: 600 }}>
                        {s.staff_name}
                        {s.staff_id == null && <span title="Not linked to a staff record" style={{ marginLeft: 6, fontSize: '0.68rem', color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 4, padding: '0 5px' }}>unlinked</span>}
                      </td>
                      <td style={{ padding: '0.45rem', textAlign: 'right' }}>{s.completed}</td>
                      <td style={{ padding: '0.45rem', textAlign: 'right' }}>{s.avg_cleaning_mins != null ? `${s.avg_cleaning_mins}m` : '-'}</td>
                      <td style={{ padding: '0.45rem', textAlign: 'right', color: s.on_time_rate != null && s.on_time_rate < 80 ? '#b91c1c' : '#166534', fontWeight: 600 }}>
                        {s.on_time_rate != null ? `${s.on_time_rate}%` : '-'}
                      </td>
                      <td style={{ padding: '0.45rem', textAlign: 'right', color: s.sla_breaches > 0 ? '#b91c1c' : 'inherit' }}>{s.sla_breaches}</td>
                      <td style={{ padding: '0.45rem', textAlign: 'right', color: s.reopened > 0 ? '#c2410c' : 'inherit' }}>{s.reopened}</td>
                      <td style={{ padding: '0.45rem', textAlign: 'right', fontWeight: 700 }}>{s.score}</td>
                    </tr>
                  );
                })}
                {(!leaderboard?.staff || leaderboard.staff.length === 0) && !lbLoading && (
                  <tr><td colSpan={8} style={{ padding: '1rem', textAlign: 'center', color: 'var(--theme-text-muted)' }}>
                    No assigned cleaning tasks in this date range. Tasks must have a housekeeper assigned to appear here.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '0.6rem', fontSize: '0.75rem', color: 'var(--theme-text-muted)' }}>
            Score = rooms cleaned ×10 − SLA breaches ×5 − re-cleans ×8. Avg time excludes tasks without a recorded start.
          </div>
        </div>
      ) : hkTab === 'sla-report' ? (
        <div style={{ marginTop: '0.8rem' }}>
          {error ? <div className="error-banner" style={{ marginBottom: '0.5rem' }}>{error}</div> : null}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>From:</label>
            <input type="date" value={srFrom} onChange={(e) => setSrFrom(e.target.value)} max={srTo}
              style={{ padding: '0.3rem 0.5rem', borderRadius: 5, border: '1px solid #cbd5e1', fontSize: '0.85rem' }} />
            <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>To:</label>
            <input type="date" value={srTo} onChange={(e) => setSrTo(e.target.value)} min={srFrom}
              style={{ padding: '0.3rem 0.5rem', borderRadius: 5, border: '1px solid #cbd5e1', fontSize: '0.85rem' }} />
            <button onClick={loadSlaReport} disabled={srLoading}
              style={{ padding: '0.35rem 1rem', borderRadius: 5, border: 'none', background: 'var(--theme-primary)', color: '#fff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
              {srLoading ? 'Loading...' : 'Load'}
            </button>
          </div>

          {slaReport?.summary && (
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
              {[
                { label: 'Breaches', val: slaReport.summary.total_breaches, bg: '#fef2f2', color: '#b91c1c' },
                { label: 'Rooms Affected', val: slaReport.summary.rooms_affected, bg: '#fff7ed', color: '#c2410c' },
                { label: 'Avg Overrun', val: slaReport.summary.avg_overrun_mins != null ? `+${slaReport.summary.avg_overrun_mins} min` : '-', bg: '#fffbeb', color: '#92400e' },
                { label: 'Worst Overrun', val: slaReport.summary.max_overrun_mins != null ? `+${slaReport.summary.max_overrun_mins} min` : '-', bg: '#faf5ff', color: '#6b21a8' },
                { label: 'Supervisor Notified', val: slaReport.summary.notified_breaches, bg: '#eff6ff', color: '#1e40af' },
              ].map((k) => (
                <div key={k.label} style={{ background: k.bg, padding: '0.5rem 1rem', borderRadius: 6, minWidth: 96 }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>{k.label}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: k.color }}>{k.val}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem', background: '#fff' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Breached At</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Room</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Room Type</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Housekeeper</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>SLA</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Elapsed</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Overrun</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>Notified</th>
                </tr>
              </thead>
              <tbody>
                {(slaReport?.rows || []).map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                    <td style={{ padding: '0.45rem', whiteSpace: 'nowrap' }}>
                      {r.breached_at ? new Date(r.breached_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                    </td>
                    <td style={{ padding: '0.45rem', fontWeight: 600 }}>{r.room_number || r.room_id}</td>
                    <td style={{ padding: '0.45rem' }}>{r.room_type_name || '-'}</td>
                    <td style={{ padding: '0.45rem' }}>{r.housekeeper || <span style={{ color: 'var(--theme-text-muted)' }}>Unassigned</span>}</td>
                    <td style={{ padding: '0.45rem', textAlign: 'right' }}>{r.sla_minutes}m</td>
                    <td style={{ padding: '0.45rem', textAlign: 'right' }}>{r.elapsed_minutes}m</td>
                    <td style={{ padding: '0.45rem', textAlign: 'right', color: '#b91c1c', fontWeight: 600 }}>+{r.overrun_minutes}m</td>
                    <td style={{ padding: '0.45rem', textAlign: 'center' }}>
                      {r.notified > 0
                        ? <span title={`${r.notified} of ${r.recipients} supervisor(s) SMSed`} style={{ color: '#166534', fontWeight: 700 }}>✓ {r.notified}</span>
                        : <span style={{ color: 'var(--theme-text-muted)' }}>—</span>}
                    </td>
                  </tr>
                ))}
                {(!slaReport?.rows || slaReport.rows.length === 0) && !srLoading && (
                  <tr><td colSpan={8} style={{ padding: '1rem', textAlign: 'center', color: 'var(--theme-text-muted)' }}>
                    No SLA breaches in this date range. 🎉
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '0.6rem', fontSize: '0.75rem', color: 'var(--theme-text-muted)' }}>
            Breaches are logged by the 5-minute checker when a cleaning task exceeds its room type's SLA. "Notified" shows how many Head-of-Housekeeping numbers were SMSed (needs the alert numbers + enabled template).
          </div>
        </div>
      ) : hkTab === 'log' ? (
        <div style={{ marginTop: '0.8rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
            <label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Filter by Room:</label>
            <select value={hkLogRoomFilter} onChange={(e) => setHkLogRoomFilter(e.target.value)} style={{ background: '#fff', minWidth: isMobile ? 0 : 140, width: isMobile ? '100%' : undefined }}>
              <option value="">All Rooms</option>
              {rooms.slice().sort((a, b) => String(a.room_number || '').localeCompare(String(b.room_number || ''), undefined, { numeric: true }))
                .map((r) => <option key={r.room_id} value={r.room_id}>{r.room_number}</option>)}
            </select>
            <button onClick={loadHkLog} disabled={hkLogLoading}>{hkLogLoading ? 'Loading...' : 'Refresh'}</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '0.86rem' }}>
              <thead>
                <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>Date / Time</th>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>Room</th>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>Status Change</th>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>Cleaned By</th>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>Changed By (User)</th>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>Source</th>
                  <th style={{ padding: '0.45rem', textAlign: 'left' }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {hkLog.map((log) => (
                  <tr key={log.log_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                    <td style={{ padding: '0.4rem', whiteSpace: 'nowrap' }}>
                      {log.changed_at ? new Date(log.changed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                    </td>
                    <td style={{ padding: '0.4rem', fontWeight: 600 }}>{log.room_number || '-'}</td>
                    <td style={{ padding: '0.4rem' }}>
                      <span style={{ color: 'var(--theme-text-muted)' }}>{log.previous_status || '?'}</span>
                      {' → '}
                      <span style={{ fontWeight: 600, color: log.new_status === 'CLEAN' ? '#166534' : '#b91c1c' }}>{log.new_status}</span>
                    </td>
                    <td style={{ padding: '0.4rem' }}>{log.changed_by_staff_name || '-'}</td>
                    <td style={{ padding: '0.4rem', color: 'var(--theme-text-secondary)' }}>{log.changed_by_user || '-'}</td>
                    <td style={{ padding: '0.4rem' }}>
                      <span style={{ fontSize: '0.78rem', padding: '1px 6px', borderRadius: 4,
                        background: log.source === 'CHECKOUT' ? '#fef3c7' : log.source === 'COMMAND_CENTER' ? '#dbeafe' : log.source === 'HOUSEKEEPING' ? '#dcfce7' : 'var(--theme-border-light)',
                        color: log.source === 'CHECKOUT' ? '#92400e' : log.source === 'COMMAND_CENTER' ? '#1e40af' : log.source === 'HOUSEKEEPING' ? '#166534' : '#475569'
                      }}>{(log.source || '').replace(/_/g, ' ')}</span>
                    </td>
                    <td style={{ padding: '0.4rem', color: 'var(--theme-text-secondary)', fontSize: '0.82rem' }}>{log.remarks || '-'}</td>
                  </tr>
                ))}
                {!hkLog.length ? <tr><td colSpan={7} style={{ padding: '0.6rem', color: 'var(--theme-text-muted)' }}>No housekeeping log entries found</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
      <>
      {error ? <div className="error-banner" style={{ marginTop: '0.5rem' }}>{error}</div> : null}
      {message ? <div className="success-banner" style={{ marginTop: '0.5rem' }}>{message}</div> : null}

      {/* ── New Dirty Rooms Alert Banner ── */}
      {newDirtyRooms.length > 0 && (
        <div style={{
          marginTop: '0.5rem', padding: '0.6rem 0.8rem', background: '#fef3c7', border: '1px solid #f59e0b',
          borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem',
          animation: 'hkAlertPulse 1.5s ease-in-out 3'
        }}>
          <div style={{ color: '#92400e', fontWeight: 600, fontSize: '0.88rem' }}>
            <span style={{ fontSize: '1.1rem', marginRight: 6 }}>!</span>
            {newDirtyRooms.length === 1
              ? `Room ${newDirtyRooms[0]} needs cleaning`
              : `${newDirtyRooms.length} rooms need cleaning: ${newDirtyRooms.join(', ')}`}
          </div>
          <button onClick={() => setNewDirtyRooms([])}
            style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid var(--theme-warning)', background: '#fff', color: '#92400e', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
            Dismiss
          </button>
        </div>
      )}
      <style>{`@keyframes hkAlertPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; box-shadow: 0 0 12px #f59e0b55; } }`}</style>

      {/* ── ER-MOM-03: SLA Breach Alert Banner ── */}
      {slaBreachedRooms.length > 0 && !slaBannerDismissed && (
        <div style={{
          marginTop: '0.5rem', padding: '0.6rem 0.8rem', background: '#fef2f2', border: '1px solid #ef4444',
          borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem',
          animation: 'hkAlertPulse 1.5s ease-in-out 3'
        }}>
          <div style={{ color: '#991b1b', fontWeight: 600, fontSize: '0.88rem' }}>
            <span style={{ fontSize: '1.1rem', marginRight: 6 }}>⏱</span>
            {slaBreachedRooms.length === 1
              ? `Room ${slaBreachedRooms[0].room_number} has exceeded its cleaning SLA (${slaBreachedRooms[0].elapsed} min / ${slaBreachedRooms[0].sla} min)`
              : `${slaBreachedRooms.length} rooms past cleaning SLA: ${slaBreachedRooms.map((s) => `${s.room_number} (${s.elapsed}/${s.sla}m)`).join(', ')}`}
            {slaApproachingCount > 0 && (
              <span style={{ color: '#92400e', fontWeight: 500 }}> · {slaApproachingCount} approaching</span>
            )}
          </div>
          <button onClick={() => setSlaBannerDismissed(true)}
            style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #ef4444', background: '#fff', color: '#991b1b', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
            Dismiss
          </button>
        </div>
      )}
      {/* Approaching-only notice (no breach yet) */}
      {slaBreachedRooms.length === 0 && slaApproachingCount > 0 && !slaBannerDismissed && (
        <div style={{
          marginTop: '0.5rem', padding: '0.5rem 0.8rem', background: '#fffbeb', border: '1px solid #f59e0b',
          borderRadius: 8, color: '#92400e', fontWeight: 600, fontSize: '0.85rem'
        }}>
          <span style={{ fontSize: '1rem', marginRight: 6 }}>⏱</span>
          {slaApproachingCount} room{slaApproachingCount > 1 ? 's' : ''} approaching cleaning SLA
        </div>
      )}

      {/* ── Live Room Rack ── */}
      <div className="card card-body" style={{ marginTop: '0.8rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{ fontWeight: 700, color: 'var(--theme-primary)', fontSize: '1rem' }}>Live Room Rack</div>
            <span style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', fontWeight: 500 }}>
              Auto-refresh in {autoRefreshCountdown}s
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.78rem', color: 'var(--theme-text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={soundEnabled} onChange={(e) => setSoundEnabled(e.target.checked)} />
              Sound
            </label>
            <button onClick={() => { load(); setAutoRefreshCountdown(AUTO_REFRESH_MS / 1000); }} disabled={loading}
              style={{ padding: '0.35rem 0.8rem', borderRadius: 6, border: '1px solid #cbd5e1', background: 'var(--theme-primary)', color: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Summary chips */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          <span style={{ ...chipBase, background: 'var(--theme-border-light)', color: '#475569', cursor: 'default' }}>Total: {rackSummary.total}</span>
          {summaryBadge('Dirty', rackSummary.dirty, '#fefce8', '#854d0e', 'DIRTY')}
          {summaryBadge('Clean', rackSummary.clean, '#dcfce7', '#166534', 'CLEAN')}
          {summaryBadge('Occupied', rackSummary.occupied, '#dbeafe', '#1e40af', 'OCCUPIED')}
          {rackSummary.ooo > 0 && <span style={{ ...chipBase, background: '#fef2f2', color: '#991b1b', cursor: 'default' }}>OOO: {rackSummary.ooo}</span>}
        </div>

        {/* Floor filter */}
        {floors.length > 1 && (
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', alignSelf: 'center' }}>Floor:</span>
            <span onClick={() => setRackFloorFilter('')}
              style={{ ...chipBase, background: !rackFloorFilter ? 'var(--theme-primary)' : 'var(--theme-border-light)', color: !rackFloorFilter ? '#fff' : '#475569' }}>All</span>
            {floors.map((f) => (
              <span key={f} onClick={() => setRackFloorFilter(rackFloorFilter === f ? '' : f)}
                style={{ ...chipBase, background: rackFloorFilter === f ? 'var(--theme-primary)' : 'var(--theme-border-light)', color: rackFloorFilter === f ? '#fff' : '#475569' }}>{f}</span>
            ))}
          </div>
        )}

        {/* Room Tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(90px, 1fr))' : 'repeat(auto-fill, minmax(120px, 1fr))', gap: isMobile ? '0.35rem' : '0.5rem' }}>
          {filteredRooms.map((r) => {
            const s = tileStyle(r);
            const isUpdating = String(r.room_id) === updatingRoomId;
            return (
              <div key={r.room_id}
                onClick={() => handleTileClick(r)}
                style={{
                  border: `2px solid ${s.border}`, borderRadius: 8, padding: '0.5rem',
                  background: s.cardBg, cursor: s.isActionable ? 'pointer' : 'not-allowed',
                  opacity: s.isActionable ? 1 : 0.55, transition: 'all 0.15s',
                  position: 'relative', textAlign: 'center',
                  ...(isUpdating ? { opacity: 0.5 } : {})
                }}>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }}>{r.room_number}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-secondary)', marginTop: 2 }}>
                  {r.room_type_name || r.room_type_code || ''}
                </div>
                <div style={{
                  marginTop: 4, padding: '2px 0', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700,
                  background: s.badgeBg, color: '#fff', letterSpacing: '0.03em'
                }}>
                  {s.label}
                </div>
                {reqByRoom[String(r.room_id)] && (() => {
                  const pb = priorityBadge(reqByRoom[String(r.room_id)].request_type);
                  return <div style={{
                    marginTop: 3, padding: '2px 0', borderRadius: 4, fontSize: '0.65rem', fontWeight: 700,
                    background: pb.bg, color: '#fff', letterSpacing: '0.03em'
                  }}>{pb.label}</div>;
                })()}
                {slaByRoom[String(r.room_id)] && (() => {
                  const sla = slaByRoom[String(r.room_id)];
                  const isBreach = sla.state === 'BREACHED';
                  return <div title={`Cleaning ${sla.elapsed} min / SLA ${sla.sla} min${sla.housekeeper ? ` · ${sla.housekeeper}` : ''}`}
                    style={{
                      marginTop: 3, padding: '2px 0', borderRadius: 4, fontSize: '0.65rem', fontWeight: 700,
                      background: isBreach ? '#dc2626' : '#f59e0b', color: '#fff', letterSpacing: '0.03em'
                    }}>{isBreach ? '⚠ ' : ''}SLA {sla.elapsed}/{sla.sla}m</div>;
                })()}
                {r.primary_guest_name && (
                  <div style={{ fontSize: '0.68rem', color: 'var(--theme-text-secondary)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.primary_guest_name}
                  </div>
                )}
              </div>
            );
          })}
          {!filteredRooms.length && <div style={{ padding: '1rem', color: 'var(--theme-text-muted)' }}>No rooms matching filter</div>}
        </div>
      </div>

      {/* ── Confirm Dialog ── */}
      {confirmRoom && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={() => setConfirmRoom(null)}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: isMobile ? '1rem' : '1.5rem', minWidth: isMobile ? 0 : 320, maxWidth: 400, width: isMobile ? 'calc(100% - 2rem)' : undefined, boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--theme-primary)', marginBottom: '0.8rem' }}>
              Confirm Status Change
            </div>
            <div style={{ marginBottom: '1rem', color: '#334155', fontSize: '0.92rem' }}>
              {String(confirmRoom.housekeeping_status || '').toUpperCase() === 'DIRTY' ? (
                <>Mark Room <strong>{confirmRoom.room_number}</strong> as <span style={{ color: '#166534', fontWeight: 700 }}>CLEAN</span>?</>
              ) : (
                <>Mark Room <strong>{confirmRoom.room_number}</strong> as <span style={{ color: '#b91c1c', fontWeight: 700 }}>DIRTY</span>?</>
              )}
            </div>
            {/* ER-MOM-04: attribute the clean to a housekeeper (optional) */}
            {String(confirmRoom.housekeeping_status || '').toUpperCase() === 'DIRTY' && hkStaffList.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.25rem' }}>Cleaned by (optional)</label>
                <select value={cleanStaffId} onChange={(e) => setCleanStaffId(e.target.value)}
                  style={{ width: '100%', padding: '0.4rem 0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem', background: '#fff' }}>
                  <option value="">— Not specified —</option>
                  {hkStaffList.map((s) => (
                    <option key={s.staff_id} value={s.staff_id}>{s.staff_name}{s.shift && s.shift !== 'GENERAL' ? ` (${s.shift})` : ''}</option>
                  ))}
                </select>
                <div style={{ fontSize: '0.72rem', color: 'var(--theme-text-muted)', marginTop: '0.25rem' }}>Credits the room to this housekeeper on the Performance leaderboard.</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmRoom(null)}
                style={{ padding: '0.4rem 1rem', borderRadius: 6, border: '1px solid #cbd5e1', background: 'var(--theme-bg-light)', color: '#475569', fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => toggleHousekeeping(confirmRoom, String(confirmRoom.housekeeping_status || '').toUpperCase() === 'DIRTY' ? cleanStaffId : null)}
                style={{
                  padding: '0.4rem 1rem', borderRadius: 6, border: 'none', fontWeight: 600, cursor: 'pointer', color: '#fff',
                  background: String(confirmRoom.housekeeping_status || '').toUpperCase() === 'DIRTY' ? '#166534' : '#f59e0b'
                }}>
                {String(confirmRoom.housekeeping_status || '').toUpperCase() === 'DIRTY' ? 'Mark CLEAN' : 'Mark DIRTY'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Requests Table ── */}
      <div className="card card-body" style={{ marginTop: '0.8rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem', flexWrap: 'wrap', gap: '0.4rem' }}>
          <div style={{ fontWeight: 700, color: 'var(--theme-primary)' }}>Priority Requests</div>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Status:</label>
            <select value={requestStatusFilter} onChange={e => setRequestStatusFilter(e.target.value)}
              style={{ padding: '0.25rem 0.5rem', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}>
              <option value="">All</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="DONE">Done</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
                <th style={{ padding: '0.4rem', textAlign: 'left' }}>Room</th>
                <th style={{ padding: '0.4rem', textAlign: 'left' }}>Request Type</th>
                <th style={{ padding: '0.4rem', textAlign: 'left' }}>Source</th>
                <th style={{ padding: '0.4rem', textAlign: 'left' }}>Priority</th>
                <th style={{ padding: '0.4rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '0.4rem', textAlign: 'left' }}>Remarks</th>
                <th style={{ padding: '0.4rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.request_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ padding: '0.4rem', fontWeight: 600 }}>{r.room_number || '-'}</td>
                  <td style={{ padding: '0.4rem' }}>{(r.request_type || '').replace(/_/g, ' ')}</td>
                  <td style={{ padding: '0.4rem' }}>
                    <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4,
                      background: r.source_module === 'GUEST_PORTAL' ? '#dbeafe' : 'var(--theme-border-light)',
                      color: r.source_module === 'GUEST_PORTAL' ? '#1e40af' : '#475569', fontWeight: 600 }}>
                      {r.source_module === 'GUEST_PORTAL' ? 'Guest' : 'Staff'}
                    </span>
                  </td>
                  <td style={{ padding: '0.4rem' }}>{r.priority}</td>
                  <td style={{ padding: '0.4rem' }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600,
                      color: r.request_status === 'OPEN' ? '#92400e' : r.request_status === 'IN_PROGRESS' ? '#1d4ed8'
                        : r.request_status === 'DONE' ? '#166534' : 'var(--theme-text-secondary)' }}>
                      {(r.request_status || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td style={{ padding: '0.4rem', color: 'var(--theme-text-secondary)', fontSize: '0.82rem' }}>{r.remarks || '-'}</td>
                  <td style={{ padding: '0.4rem' }}>
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                      {r.request_status === 'OPEN' && (
                        <button onClick={() => runRequestAction(r.request_id, 'IN_PROGRESS')}
                          style={{ fontSize: '0.75rem', padding: '2px 8px', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Start</button>
                      )}
                      {['OPEN', 'IN_PROGRESS'].includes(r.request_status) && (
                        <button onClick={() => runRequestAction(r.request_id, 'DONE')}
                          style={{ fontSize: '0.75rem', padding: '2px 8px', background: '#166534', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Done</button>
                      )}
                      {['OPEN', 'IN_PROGRESS'].includes(r.request_status) && (
                        <button onClick={() => runRequestAction(r.request_id, 'CANCELLED')}
                          style={{ fontSize: '0.75rem', padding: '2px 8px', background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
                      )}
                      {['DONE', 'CANCELLED'].includes(r.request_status) && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)' }}>--</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!requests.length ? <tr><td colSpan={7} style={{ padding: '0.55rem', color: 'var(--theme-text-muted)' }}>No requests matching filter</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

export default PmsHousekeeping;
